import React, { useMemo, useState } from 'react';
import {
  BarList, Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, useAdminGet,
} from '../kit';

/**
 * Step timings — "what took what" across the search pipeline, aggregated.
 *
 * Reads search/explorer_stats (admin_search_explorer.py), which groups the
 * search_query_logs rows by route / engine / backend / family / day / hour /
 * user and computes avg, p50, p95 and max for every normalised step in SQL.
 * Cache hits are left out of the percentiles unless family=cache is asked
 * for explicitly: their step timings are copies of the original run and
 * their total is the hit's own ~8 ms, so they would drag every step to zero.
 *
 * This file also owns the timing primitives the Query explorer shares — the
 * step vocabulary, the five-band stacked bar and the row-click wrapper — so
 * both tabs draw a millisecond the same way.
 */

// ---------------------------------------------------------------------------
// Shared: step vocabulary, formatting, the stacked bar
// ---------------------------------------------------------------------------
/** Normalised step names in pipeline order, as normalise_timings emits them. */
export const STEP_ORDER = [
  'total_ms', 'entitlements_ms', 'understanding_ms', 'classify_ms', 'filters_ms', 'expand_ms',
  'retrieve_ms', 'typesense_ms', 'pinecone_ms', 'rescore_ms', 'hydrate_ms', 'other_ms',
];
export const STEP_LABELS: Record<string, string> = {
  total_ms: 'Total', entitlements_ms: 'Entitlements', understanding_ms: 'Understanding',
  classify_ms: 'Classify', filters_ms: 'Filters', expand_ms: 'Expand', retrieve_ms: 'Retrieve',
  typesense_ms: 'Typesense', pinecone_ms: 'Pinecone', rescore_ms: 'Rescore', hydrate_ms: 'Hydrate',
  other_ms: 'Other',
};
/** Sub-steps nest under the step whose time already includes them: classify /
 *  filters / expand run inside understanding, the two legs inside retrieve.
 *  A breakdown indents them; a bar must not count parent and child both. */
export const STEP_PARENT: Record<string, string> = {
  classify_ms: 'understanding_ms', filters_ms: 'understanding_ms', expand_ms: 'understanding_ms',
  typesense_ms: 'retrieve_ms', pinecone_ms: 'retrieve_ms',
};
/** The parent of a step on ONE row: on the typesense route the speculative
 *  Typesense call ran inside understanding, and the server flags such a row
 *  (`retrieve_inside_understanding`) so retrieve nests there too. */
export const stepParent = (step: string, timings: Record<string, any> | null | undefined): string | undefined =>
  step === 'retrieve_ms' && timings?.retrieve_inside_understanding ? 'understanding_ms' : STEP_PARENT[step];
/** The steps a cache hit copied from the run that filled the cache — the
 *  server names them; an older payload with only the boolean flag is read as
 *  "everything but the hit's own total, entitlements and other". */
export const copiedSteps = (timings: Record<string, any> | null | undefined): string[] => {
  if (Array.isArray(timings?.copied_steps)) return timings!.copied_steps.map(String);
  if (!timings?.from_original_run) return [];
  return STEP_ORDER.filter(s => !['total_ms', 'entitlements_ms', 'other_ms'].includes(s));
};

/** The five bands of the stacked bar, in pipeline order. */
export const BANDS: { key: string; label: string; cls: string }[] = [
  { key: 'understanding_ms', label: 'understanding', cls: 'bg-secondary-400' },
  { key: 'retrieve_ms', label: 'retrieve', cls: 'bg-primary-500' },
  { key: 'rescore_ms', label: 'rescore', cls: 'bg-amber-500' },
  { key: 'hydrate_ms', label: 'hydrate', cls: 'bg-green-500' },
  { key: 'other_ms', label: 'other', cls: 'bg-grayscale-600' },
];

export const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
export const fmtMs = (v: any, dp = 0): string => {
  const n = num(v);
  return n === null ? '—' : `${n.toLocaleString(undefined, { maximumFractionDigits: dp })} ms`;
};
export const fmtPct = (part: any, whole: any): string => {
  const p = num(part); const w = num(whole);
  return p === null || !w ? '—' : `${Math.round((p / w) * 100)}%`;
};

export type Band = { key: string; label: string; cls: string; ms: number };

/** Splits step milliseconds into the five bands. `other` is the total minus
 *  the four named bands rather than the server's other_ms, so the bar always
 *  adds up to the total it is drawn against — a legacy row's other_s and a
 *  v2 row's leftover are not computed the same way server-side. When the
 *  named bands exceed the total (a copied cache-hit breakdown against the
 *  hit's own 8 ms) the bar is drawn against their sum instead of overflowing.
 *  A retrieve that ran inside understanding is not a band: its time is
 *  already in the understanding band. */
export function toBands(get: (step: string) => number | null, total: number | null,
                        timings?: Record<string, any> | null): { total: number; bands: Band[] } {
  const t = Math.max(0, total ?? 0);
  const named = BANDS.slice(0, 4).map(b => ({
    ...b, ms: stepParent(b.key, timings) ? 0 : Math.max(0, get(b.key) ?? 0),
  }));
  const used = named.reduce((a, b) => a + b.ms, 0);
  const other = { ...BANDS[4], ms: Math.max(0, t - used) };
  return { total: Math.max(t, used), bands: [...named, other] };
}

export const StackedBar: React.FC<{ bands: Band[]; total: number; title?: string; className?: string }> =
  ({ bands, total, title, className }) => (
  <div title={title} className={`flex h-2.5 w-full rounded-full bg-grayscale-200 overflow-hidden ${className || ''}`}>
    {total > 0 && bands.map(b => b.ms > 0 && (
      <div key={b.key} className={`h-full ${b.cls}`} style={{ width: `${(b.ms / total) * 100}%` }} />
    ))}
  </div>
);

export const BandLegend: React.FC = () => (
  <div className="flex items-center gap-3 flex-wrap text-[11px] text-grayscale-1100">
    {BANDS.map(b => (
      <span key={b.key} className="inline-flex items-center gap-1">
        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${b.cls}`} />{b.label}
      </span>
    ))}
  </div>
);

/** DataTable has no row-click prop and the kit is shared by every panel, so
 *  a row's rendered cell carries data-row-key and this wrapper turns a click
 *  anywhere on that row — buttons, links and inputs excepted, they have their
 *  own jobs — into onRow(key). */
export const RowClick: React.FC<{ onRow: (key: string) => void; className?: string; children: React.ReactNode }> =
  ({ onRow, className, children }) => (
  <div className={`[&_tbody_tr]:cursor-pointer ${className || ''}`}
    onClick={e => {
      const el = e.target as HTMLElement;
      if (el.closest('button, a, input, select, textarea')) return;
      const tagged = el.closest('tbody tr')?.querySelector('[data-row-key]') as HTMLElement | null;
      const key = tagged?.dataset.rowKey;
      if (key !== undefined) onRow(key);
    }}>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
const GROUP_BYS = ['route', 'engine', 'backend', 'family', 'day', 'hour', 'user'];
// "hour" is date_trunc('hour') server-side — a timeline of calendar hours,
// newest first, not 24 buckets of the clock.
const GROUP_LABELS: Record<string, string> = {
  route: 'By route', engine: 'By engine', backend: 'By backend', family: 'By family',
  day: 'By day', hour: 'By hour (timeline)', user: 'By user',
};
const STATS = ['avg', 'p50', 'p95', 'max'];
const STAT_LABELS: Record<string, string> = { avg: 'Average', p50: 'Median (p50)', p95: 'p95', max: 'Max' };
const FAMILIES = ['', 'v2', 'cache', 'hybrid'];
const FAMILY_LABELS: Record<string, string> = {
  '': 'Any family', v2: 'v2', cache: 'Cache hits', hybrid: 'Legacy hybrid',
};
const ROUTES = ['', 'typesense', 'engine', 'sql', 'deep', 'filter_browse', 'hybrid'];
const FIXED_COLS = ['key', 'n', 'errors', 'cached', 'avg_results', 'share'];
const FILTER_LABELS: Record<string, string> = {
  family: 'family', route: 'route', engine: 'engine', from: 'from', to: 'to',
};

/** A group's stats for one step. The contract keys them by step name on the
 *  group; a `steps` sub-object is tolerated in case the server nests them. */
const stepStat = (g: any, step: string): any =>
  (g?.steps && typeof g.steps === 'object' ? g.steps[step] : g?.[step]) ?? null;

const StepTimings: React.FC = () => {
  const [groupBy, setGroupBy] = useState('route');
  const [stat, setStat] = useState('avg');
  const [f, setF] = useState({ family: '', route: '', engine: '', from: '', to: '' });
  // null = nothing selected. A group key can be '' (legacy rows have no
  // engine), so the empty string cannot double as the sentinel.
  const [sel, setSel] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const params = useMemo(() => ({ group_by: groupBy, ...f }), [groupBy, f]);
  const key = JSON.stringify(params);
  const stats = useAdminGet('search', 'explorer_stats', params, [key, tick]);
  // Engines are not a fixed list (the admin switch can add one), so the
  // options are the engines the log has seen — read once, whatever the
  // grouping — plus the filter in force.
  const facets = useAdminGet('search', 'explorer_facets', {}, []);

  const groups: any[] = stats.data?.groups || [];
  const steps: string[] = stats.data?.steps?.length ? stats.data.steps : STEP_ORDER;
  // With cache hits in the percentiles only the hit's own steps (total,
  // entitlements, other) have a share; the copied ones come back null and
  // the phase bar, which would be the original run's, is not drawn.
  const cacheIncluded = !!stats.data?.cache_included;
  const engines = useMemo(() => {
    const seen: string[] = (facets.data?.engines || []).map((a: any) => String(a.value ?? '')).filter(Boolean);
    return ['', ...(f.engine && !seen.includes(f.engine) ? [f.engine] : []), ...seen];
  }, [facets.data, f.engine]);

  const rows = useMemo(() => groups.map(g => {
    const row: Record<string, any> = {
      key: String(g.key ?? ''), n: num(g.n), errors: num(g.errors), cached: num(g.cached),
      avg_results: num(g.avg_results), share: '', _g: g,
    };
    for (const s of steps) row[s] = num(stepStat(g, s)?.[stat]);
    return row;
  }), [groups, steps, stat]);
  const nestedTotal = groups.reduce((a, g) => a + (num(g.retrieve_nested) ?? 0), 0);

  const labels = useMemo(() => {
    const out: Record<string, string> = {
      key: groupBy.charAt(0).toUpperCase() + groupBy.slice(1), n: 'Queries', errors: 'Errors',
      cached: 'Cached', avg_results: 'Avg results', share: 'Share of total',
    };
    for (const s of steps) out[s] = STEP_LABELS[s] || s;
    return out;
  }, [groupBy, steps]);

  const selected = sel === null ? undefined : groups.find(g => String(g.key ?? '') === sel);
  const selRows = useMemo(() => !selected ? [] : steps.map(s => {
    const st = stepStat(selected, s) || {};
    return {
      step: STEP_LABELS[s] || s, avg: num(st.avg), p50: num(st.p50), p95: num(st.p95), max: num(st.max),
      share: num(st.share), _key: s,
    };
  }), [selected, steps]);

  // The bar is drawn from the server's `share` (SUM(step) / SUM(total) over
  // the same rows, a retrieve nested in understanding contributing nothing),
  // scaled to the average total: share × avg total is the step's average
  // with 0 for the rows that did not run it. Averages of the steps alone
  // would be over different row sets and could exceed the total.
  const bandsFor = (g: any) => {
    const total = num(stepStat(g, 'total_ms')?.avg);
    return toBands(s => {
      const share = num(stepStat(g, s)?.share);
      return share !== null && total !== null ? share * total : num(stepStat(g, s)?.avg);
    }, total);
  };
  const shareTitle = (g: any) => {
    const { total, bands } = bandsFor(g);
    const nested = num(g?.retrieve_nested) ?? 0;
    return bands.map(b => `${b.label}: ${fmtMs(b.ms)} (${fmtPct(b.ms, total)})`).join('\n')
      + (nested ? `\nretrieve ran inside understanding on ${nested} of ${num(g?.n_timed) ?? '?'} (speculative Typesense)` : '');
  };
  const active = Object.entries(f).filter(([, v]) => v);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Where the time goes"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {active.length > 0 && (
              <Btn small onClick={() => setF({ family: '', route: '', engine: '', from: '', to: '' })}>
                Clear {active.length} filter{active.length === 1 ? '' : 's'}
              </Btn>
            )}
            <Btn small onClick={() => setTick(t => t + 1)}>Refresh</Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={groupBy} onChange={v => { setGroupBy(v); setSel(null); }} options={GROUP_BYS} labels={GROUP_LABELS} />
          <Select value={stat} onChange={setStat} options={STATS} labels={STAT_LABELS} />
          <Select value={f.family} onChange={set('family')} options={FAMILIES} labels={FAMILY_LABELS} />
          <Select value={f.route} onChange={set('route')}
            options={[...ROUTES, ...(f.route && !ROUTES.includes(f.route) ? [f.route] : [])]}
            labels={{ '': 'Any route' }} />
          <Select value={f.engine} onChange={set('engine')} options={engines} labels={{ '': 'Any engine' }} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
        </div>
        {active.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {active.map(([k, v]) => (
              <Chip key={k} label={FILTER_LABELS[k] || k} value={v} onClear={() => set(k as keyof typeof f)('')} />
            ))}
          </div>
        )}
        <p className="text-xs text-grayscale-1100">
          Each cell is the {STAT_LABELS[stat].toLowerCase()} of that step in ms, over the rows that
          ran it. The share bar splits the total into understanding · retrieve · rescore · hydrate ·
          other, summed over the same rows so the pieces add up; a retrieve that ran inside
          understanding (the typesense route's speculative call{nestedTotal ? `, ${nestedTotal.toLocaleString()} rows here` : ''})
          counts in understanding, not twice. Cache hits are excluded from the percentiles unless
          the filters ask for them — their breakdowns are copies of the original run. Click a row
          for every statistic of every step.
        </p>
        <BandLegend />
        {stats.error && <Msg kind="error">{stats.error}</Msg>}
        {stats.loading && !stats.data ? <Loading /> : (
          <RowClick onRow={k => setSel(s => s === k ? null : k)}>
            <DataTable rows={rows} columns={[...FIXED_COLS, ...steps]} labels={labels}
              csvName={`step_timings_${groupBy}_${stat}`} defaultSort="-n" maxHeight="32rem"
              empty={active.length ? 'Nothing matches these filters.' : 'No search timings logged yet.'}
              render={{
                key: r => (
                  <span data-row-key={r.key}
                    className={`font-medium ${sel !== null && sel === r.key ? 'text-primary-700' : 'text-grayscale-1300'}`}>
                    {r.key || <span className="text-grayscale-1100 font-normal">(none)</span>}
                  </span>
                ),
                n: r => r.n === null ? '—' : r.n.toLocaleString(),
                errors: r => r.errors ? <span className="text-red-600">{r.errors.toLocaleString()}</span> : '0',
                cached: r => r.cached === null ? '—' : r.cached.toLocaleString(),
                avg_results: r => r.avg_results === null ? '—' : r.avg_results.toLocaleString(undefined, { maximumFractionDigits: 1 }),
                share: r => {
                  if (cacheIncluded) return <span className="text-xs text-grayscale-1100" title="Cache hits are in these numbers; their steps are copies of the original run">n/a</span>;
                  const { total, bands } = bandsFor(r._g);
                  const nested = num(r._g?.retrieve_nested) ?? 0;
                  return (
                    <span className="inline-flex items-center gap-1.5">
                      <StackedBar bands={bands} total={total} title={shareTitle(r._g)} className="w-40" />
                      {nested > 0 && <span className="text-[10px] text-grayscale-1100 whitespace-nowrap" title={`retrieve ran inside understanding on ${nested} rows`}>{nested} nested</span>}
                    </span>
                  );
                },
                ...Object.fromEntries(steps.map(s => [s, (r: Record<string, any>) => (
                  <span className={s === 'total_ms' ? 'font-medium' : STEP_PARENT[s] ? 'text-grayscale-1100' : ''}>
                    {fmtMs(r[s])}
                  </span>
                )])),
              }} />
          </RowClick>
        )}
      </Section>

      {selected && (
        <Section title={`${labels.key}: ${sel || '(none)'} — every statistic per step`}
          right={<Btn small onClick={() => setSel(null)}>Close</Btn>}>
          {(num(selected.retrieve_nested) ?? 0) > 0 && (
            <p className="text-xs text-grayscale-1100">
              On {Number(selected.retrieve_nested).toLocaleString()} of {Number(selected.n_timed ?? 0).toLocaleString()} timed
              rows retrieve ran inside understanding (speculative Typesense): its statistics below are its own
              cost on every row, its share only counts the rows where it was a phase of its own.
            </p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DataTable rows={selRows} columns={['step', 'avg', 'p50', 'p95', 'max', 'share']}
              labels={{ step: 'Step', avg: 'Average', p50: 'p50', p95: 'p95', max: 'Max', share: 'Share' }}
              csvName={`step_timings_${groupBy}_${sel || 'none'}`} maxHeight="24rem"
              render={{
                step: r => <span className={STEP_PARENT[r._key] ? 'pl-4 text-grayscale-1100' : 'font-medium'}>{r.step}</span>,
                avg: r => fmtMs(r.avg, 1), p50: r => fmtMs(r.p50, 1), p95: r => fmtMs(r.p95, 1), max: r => fmtMs(r.max, 1),
                share: r => r.share === null ? '—' : `${Math.round(r.share * 100)}%`,
              }} />
            <div>
              <div className="text-xs text-grayscale-1100 mb-2">{STAT_LABELS[stat]} per step, ms</div>
              <BarList items={selRows.filter(r => r.step !== 'Total' && r[stat as 'avg'] !== null)
                .map(r => ({ label: r.step, value: Math.round(r[stat as 'avg'] as number) }))}
                total={num(stepStat(selected, 'total_ms')?.[stat]) ?? undefined} />
            </div>
          </div>
        </Section>
      )}
    </div>
  );
};

export default StepTimings;
